import { SurveyConfiguration } from "@rubi/api-generated";
import { toast } from "@rubi/ui";

const SERVICES_DOMAIN = "ourcommunitysafety";
const DEFAULT_API_REGION = "ca";

interface ResolveApiRegionArgs {
  sessionApiRegion?: string | null;
  organizationApiRegion?: string | null;
  userApiRegion?: string | null;
}

export const resolveApiRegion = ({
  sessionApiRegion,
  organizationApiRegion,
  userApiRegion,
}: ResolveApiRegionArgs): string => {
  const region = sessionApiRegion ?? organizationApiRegion ?? userApiRegion;
  return region === "us" ? "us" : DEFAULT_API_REGION;
};

/**
 * Returns the services app base host (no path) for the given api_region and environment.
 * api_region from rubi_user: "ca" -> .ca, "us" -> .com.
 */
export const getServicesBaseHost = (
  apiRegion: string,
  isDevEnv: boolean,
): string => {
  const tld = apiRegion === "us" ? "com" : "ca";
  const subdomain = isDevEnv ? "test." : "www.";
  return `${subdomain}${SERVICES_DOMAIN}.${tld}`;
};

export const generateTextMessageBody = (
  reportName: string,
  callerFirstName: string,
  callerLastName: string,
  servicesURLSlug: string,
  selectedReport: SurveyConfiguration,
  fullName: string,
  isDevEnv: boolean,
  referenceId: string,
  apiRegion: string,
) => {
  const buildBaseURL = () => {
    const urlSlug = servicesURLSlug;
    if (!urlSlug) {
      toast.error(
        "❌ The organization does not have a valid URL slug, please contact support to update the configuration for this organization.",
      );
      return;
    }

    const baseHost = getServicesBaseHost(apiRegion, isDevEnv);
    return `https://${baseHost}/${urlSlug}/online-reports/report/`;
  };
  const reportNameIncludesReport = reportName.toLowerCase().includes("report");
  const reportNameIncludesProgram = reportName
    .toLowerCase()
    .includes("program");
  const reportNameIncludesForm = reportName.toLowerCase().includes("form");

  return `Hi${callerFirstName ? ` ${callerFirstName}` : ""}${
    callerLastName ? ` ${callerLastName}` : ""
  },\n\nPlease use the following link to submit a ${
    reportNameIncludesReport
      ? "report"
      : reportNameIncludesProgram
        ? "program"
        : reportNameIncludesForm
          ? "form"
          : "report"
  }: \n\n${buildBaseURL()}${selectedReport?.survey_class}?reference_id=${referenceId}\n\n${fullName}`;
};

export const generateGeneralReportingTextMessageBody = (
  callerFirstName: string,
  callerLastName: string,
  servicesURLSlug: string,
  fullName: string,
  isDevEnv: boolean,
  referenceId: string,
  apiRegion: string = DEFAULT_API_REGION,
) => {
  if (!servicesURLSlug) {
    toast.error(
      "❌ The organization does not have a valid URL slug, please contact support to update the configuration for this organization.",
    );
    return "";
  }

  const baseHost = getServicesBaseHost(apiRegion, isDevEnv);
  const url = `https://${baseHost}/${servicesURLSlug}/online-reports/?reference_id=${referenceId}`;

  return `Hi${callerFirstName ? ` ${callerFirstName}` : ""}${
    callerLastName ? ` ${callerLastName}` : ""
  },\n\nPlease use the following link to file a report: \n\n${url}\n\n${fullName}`;
};
